import React, { useEffect, useRef, useState } from 'react';
import { CheckOutlined, DownOutlined } from '@ant-design/icons';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { WorkspaceDefinition, WorkspaceId } from '../constants/workspaces';

interface WorkspaceSwitcherProps {
  /** `header` = compact GetStream-style control; `sidebar` = full-width card */
  variant?: 'header' | 'sidebar';
}

const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  variant = 'sidebar',
}) => {
  const { workspace, workspaces, workspaceId, setWorkspaceId } = useWorkspace();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const ActiveIcon = workspace.icon;
  const isHeader = variant === 'header';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const handleSelect = (id: WorkspaceId) => {
    setWorkspaceId(id, { navigateHome: id !== workspaceId });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${isHeader ? '' : 'mb-3'}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={workspace.label}
        title={workspace.label}
        className={
          isHeader
            ? // Content-sized (no max-width / ellipsis). Below lg, label hides → icon + chevron.
              'flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg border border-border bg-background px-2 text-left transition-colors hover:bg-background-tertiary lg:gap-2.5 lg:px-2.5'
            : 'flex w-full items-center gap-3 rounded-2xl bg-slate-50 px-2.5 py-2.5 text-left ring-1 ring-slate-200/80 transition-all hover:bg-slate-100 hover:ring-slate-300 dark:bg-slate-900/50 dark:ring-slate-600 dark:hover:bg-slate-900 dark:hover:ring-slate-500'
        }
      >
        <span
          className={`flex flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white ${
            isHeader ? 'h-7 w-7' : 'h-9 w-9 rounded-xl shadow-sm shadow-blue-500/25'
          }`}
        >
          <ActiveIcon style={{ fontSize: isHeader ? 13 : 15 }} />
        </span>
        {isHeader ? (
          <>
            <span className="hidden whitespace-nowrap text-[13px] font-semibold text-foreground lg:inline">
              {workspace.label}
            </span>
            <DownOutlined
              className="flex-shrink-0 text-foreground-muted"
              style={{ fontSize: 10 }}
            />
          </>
        ) : (
          <>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold leading-snug text-foreground">
                {workspace.label}
              </span>
              <span className="mt-0.5 block text-[11px] text-foreground-muted">
                Switch workspace
              </span>
            </span>
            <DownOutlined
              className="flex-shrink-0 text-foreground-muted"
              style={{ fontSize: 12 }}
            />
          </>
        )}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Workspaces"
          className={`absolute z-50 mt-2 overflow-hidden rounded-2xl border border-border bg-background-secondary shadow-xl shadow-black/10 ${
            isHeader ? 'left-0 w-[320px]' : 'left-0 right-0'
          }`}
        >
          <div className="border-b border-border/70 px-3.5 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-muted">
              Workspaces
            </p>
          </div>
          <ul className="max-h-80 overflow-y-auto p-1.5">
            {workspaces.map((item: WorkspaceDefinition) => {
              const isActive = item.id === workspaceId;
              const ItemIcon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelect(item.id)}
                    className={`w-full rounded-xl px-2.5 py-2.5 text-left transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-slate-800'
                        : 'hover:bg-background'
                    }`}
                  >
                    <span className="flex items-start gap-2.5">
                      <span
                        className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${
                          isActive
                            ? 'bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-sm shadow-blue-500/20'
                            : 'bg-background text-foreground-secondary ring-1 ring-border'
                        }`}
                      >
                        <ItemIcon style={{ fontSize: 14 }} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold text-foreground">
                          {item.label}
                        </span>
                        <span className="mt-0.5 block text-[11px] leading-snug text-foreground-muted">
                          {item.description}
                        </span>
                      </span>
                      {isActive && (
                        <CheckOutlined
                          className="mt-1 text-accent-primary dark:text-[#D9F42B]"
                          style={{ fontSize: 12 }}
                        />
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
